<script setup lang="ts">
import LeButton from "@dsk/components/Button/LeButton.vue";
import LeHeading from "@dsk/components/Heading/LeHeading.vue";
import LeChevronRight from "@dsk/icons/LeChevronRight.vue";
import { ref } from "vue";

import type { DownloadSection } from "@/types";

import LeMsiDownloadModal from "./LeMsiDownloadModal.vue";

interface Link {
  type: "url" | "msi-modal";
  text: string;
  onClick: () => void;
}

interface MsiModalLink extends Link {
  type: "msi-modal";
}

interface UrlLink extends Link {
  type: "url";
  url: string;
}

type LinkProp = MsiModalLink | UrlLink;

interface SectionLink {
  text: string;
  to: DownloadSection;
}

defineProps<{
  title: string;
  links: LinkProp[];
  sectionLink: SectionLink;
}>();

const emit = defineEmits<{
  readMoreClick: [];
}>();

const isMsiDownloadModalOpen = ref<boolean>(false);

const onReadMoreClick = () => {
  emit("readMoreClick");
};
</script>

<template>
  <div class="le-download-option">
    <slot name="illustration"></slot>

    <div class="le-download-option__container">
      <LeHeading tag="h2" size="m">{{ title }}</LeHeading>

      <ul class="le-download-option__links">
        <li v-for="link in links" :key="link.text">
          <a
            v-if="link.type === 'url'"
            :href="link.url"
            target="_blank"
            class="le-download-option__link"
            @click="link.onClick()"
          >
            {{ link.text }}
          </a>
          <a
            v-else-if="link.type === 'msi-modal'"
            href="#"
            class="le-download-option__link"
            @click="
              link.onClick();
              isMsiDownloadModalOpen = true;
            "
          >
            {{ link.text }}
          </a>

          <LeMsiDownloadModal
            v-if="link.type === 'msi-modal'"
            :is-open="isMsiDownloadModalOpen"
            @close="isMsiDownloadModalOpen = false"
          />
        </li>
      </ul>

      <LeButton tag="router-link" :to="{ hash: sectionLink.to }" variant="outline" @click="onReadMoreClick()">
        {{ sectionLink.text }}
        <LeChevronRight role="presentation" />
      </LeButton>
    </div>
  </div>
</template>

<style lang="less" scoped>
@import "@dsk/styles/all";

.le-download-option {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 12px;

  #media-breakpoint-up(md, {
    flex-direction: row;
    align-items: center;
    text-align: start;
    gap: 24px;
  });

  #media-breakpoint-up(lg, {
    flex-direction: column;
    align-items: start;
    gap: 12px;
  });

  &__links {
    display: flex;
    flex-direction: column;
    margin: 12px 0 38px 0;
    padding: 0;
    list-style: none;
    gap: 12px;

    #media-breakpoint-up(md, {
      height: 94px;
    });
  }

  &__link {
    .body-text("m");
    .link();
  }
}
</style>
